import {useCallback} from 'react';

import {useAccessToken} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {PAYPAL_SERVER_ROUTES} from '../paypal-constants';
import {logPayPalClientError} from '../utils/paypal-utils';

/**
 * Notify server to release PayPal staging on basket (cancel / failure).
 *
 * @returns {{
 *   cancelPayPalFlow: (basketId: string) => Promise<void>,
 *   invalidatePayPalFlow: (basketId: string) => Promise<void>,
 * }}
 */
export function usePayPalFlowInvalidate() {
  const {site} = useMultiSite();
  const {getTokenWhenReady} = useAccessToken();

  const notifyPayPalEndpoint = useCallback(
    async (endpoint, basketId) => {
      if (!basketId || !site?.id) {
        return;
      }

      try {
        const token = await getTokenWhenReady();

        await fetch(`${PAYPAL_SERVER_ROUTES.BASE}/${endpoint}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({basketId, siteId: site.id}),
        });
      } catch (error) {
        logPayPalClientError(`${endpoint}/notify-failed`, {error, basketId});
      }
    },
    [getTokenWhenReady, site?.id]
  );

  const cancelPayPalFlow = useCallback(
    (basketId) => notifyPayPalEndpoint('cancel', basketId),
    [notifyPayPalEndpoint]
  );

  const invalidatePayPalFlow = useCallback(
    (basketId) => notifyPayPalEndpoint('failure', basketId),
    [notifyPayPalEndpoint]
  );

  return {cancelPayPalFlow, invalidatePayPalFlow};
}

export default usePayPalFlowInvalidate;
